import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PageBackground from "@/components/PageBackground";
import SEO from "@/components/SEO";
import RaceMap, { defaultLocations, type EventCategory } from "@/components/RaceMap";
import TrackThumb from "@/components/TrackThumb";
import { ExternalLink, Building2, Flag } from "lucide-react";

const categoryColors: Record<EventCategory, string> = {
  Messe: "#22c55e",
  Motorsport: "#f59e0b",
};

// Parse first day of date string like "14.05.2026" or "29.–31.05.2026" or "14.–18.08.2026"
const parseStart = (d?: string): number => {
  if (!d) return 0;
  const m = d.match(/^(\d{1,2})[^\d]*?(?:\d{1,2})?[.\-–\s]*?(\d{1,2})\.(\d{4})/);
  if (!m) return 0;
  const [, day, month, year] = m;
  return new Date(Number(year), Number(month) - 1, Number(day)).getTime();
};

const Kalender = () => {
  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <PageBackground />
      <div className="relative z-10">
      <SEO
        title="Kalender | 4FaceMedia"
        description="Termine & Events von 4FaceMedia im Überblick."
        path="/kalender"
      />
      <Header />
      <main className="pt-32 pb-24">
        <div className="container mx-auto px-6">
          <h1 className="font-agency font-black italic uppercase text-5xl md:text-7xl tracking-wide mb-4">
            Kalender
          </h1>
          <p className="text-foreground/70 font-light text-lg mb-12 max-w-2xl">
            Hier findest du bald alle wichtigen Termine, Shootings & Events.
          </p>

          <div className="mb-16">
            <h2 className="font-agency font-black italic uppercase text-3xl md:text-4xl tracking-wide mb-6">
              Standorte
            </h2>
            <RaceMap />
            <div className="flex flex-wrap gap-4 mt-4 text-sm text-foreground/70 font-light">
              <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#22c55e]" /> Messe</span>
              <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-[#f59e0b]" /> Motorsport</span>
            </div>
          </div>

          {(["Messe", "Motorsport"] as EventCategory[]).map((cat) => {
            const items = defaultLocations
              .filter((e) => e.category === cat)
              .sort((a, b) => parseStart(a.date) - parseStart(b.date));
            if (items.length === 0) return null;
            return (
              <div key={cat} className="mb-12">
                <div className="flex items-center gap-3 mb-6">
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ background: categoryColors[cat] }}
                  />
                  <h2 className="font-agency font-black italic uppercase text-3xl md:text-4xl tracking-wide">
                    {cat}
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {items.map((ev, i) => {
                    const showTrack =
                      ev.category === "Motorsport" &&
                      ev.trackId &&
                      ev.trackId !== "luedinghausenOval";
                    const Inner = (
                      <>
                        <div className="absolute inset-0 opacity-25 pointer-events-none flex items-center justify-center">
                          {showTrack ? (
                            <TrackThumb
                              trackId={ev.trackId!}
                              className="w-full h-full"
                            />
                          ) : ev.category === "Motorsport" ? (
                            <Flag
                              className="w-24 h-24"
                              style={{ color: categoryColors[ev.category] }}
                              strokeWidth={1}
                            />
                          ) : (
                            <Building2
                              className="w-24 h-24"
                              style={{ color: categoryColors[ev.category] }}
                              strokeWidth={1}
                            />
                          )}
                        </div>
                        {ev.url && (
                          <ExternalLink
                            className="absolute top-3 right-3 w-4 h-4 text-foreground/50 group-hover:text-foreground transition-colors"
                            strokeWidth={1.5}
                          />
                        )}
                        <div className="relative flex flex-col h-full p-5 min-h-[200px]">
                          <span className="font-agency font-black italic uppercase tracking-wide text-sm text-foreground/70 mb-2">
                            {ev.date ?? "—"}
                          </span>
                          <span className="font-agency font-black italic uppercase tracking-wide text-2xl text-foreground leading-tight mb-auto">
                            {ev.name}
                          </span>
                        </div>
                      </>
                    );
                    const cardClass =
                      "group relative block rounded-2xl border border-border/30 bg-foreground/[0.02] overflow-hidden hover:border-foreground/30 hover:bg-foreground/[0.05] transition-colors";
                    return ev.url ? (
                      <a
                        key={i}
                        href={ev.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cardClass}
                      >
                        {Inner}
                      </a>
                    ) : (
                      <div key={i} className={cardClass}>
                        {Inner}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </main>
      <Footer />
      </div>
    </div>
  );
};

export default Kalender;
